import { primaryTokens } from '@features/tenant/branding';
import type { LauncherTheme } from '@features/tenant/launcherTheme';
import type { ColorScheme } from '@features/tenant/colorScheme';

export type ThemeTokens = Record<string, string>;

type SchemePalette = Readonly<Record<ColorScheme, ThemeTokens>>;

const SURFACE_ALIASES: ThemeTokens = {
  '--foreground': 'var(--text-primary)',
  '--card': 'var(--surface)',
  '--card-foreground': 'var(--text-primary)',
  '--popover': 'var(--surface)',
  '--popover-foreground': 'var(--text-primary)',
  '--secondary': 'var(--surface-elevated)',
  '--secondary-foreground': 'var(--text-primary)',
  '--muted': 'var(--surface-elevated)',
  '--muted-foreground': 'var(--text-muted)',
  '--input': 'var(--border)',
  '--destructive': 'var(--danger)',
};

const MINIMAL: SchemePalette = {
  dark: {
    '--background': '240 10% 4%',
    '--foreground': '0 0% 98%',
    '--card': '240 10% 8%',
    '--card-foreground': '0 0% 98%',
    '--popover': '240 10% 8%',
    '--popover-foreground': '0 0% 98%',
    '--secondary': '240 10% 14%',
    '--secondary-foreground': '0 0% 98%',
    '--muted': '240 10% 14%',
    '--muted-foreground': '240 5% 60%',
    '--border': '240 10% 18%',
    '--input': '240 10% 18%',
    '--destructive': '0 84% 60%',
    '--success': '152 65% 45%',
    '--warning': '38 92% 55%',
    '--danger': '0 75% 55%',
    '--info': '217 91% 62%',
    '--surface': '240 10% 8%',
    '--surface-elevated': '240 10% 14%',
    '--text-primary': '0 0% 98%',
    '--text-secondary': '240 6% 80%',
    '--text-muted': '240 5% 60%',
    '--border-strong': '240 10% 28%',
    '--overlay': '240 10% 2%',
    '--ranking-gold': '43 96% 56%',
    '--ranking-silver': '240 5% 60%',
    '--ranking-bronze': '25 75% 47%',
    '--sidebar-background': '240 10% 6%',
    '--sidebar-foreground': '0 0% 98%',
    '--sidebar-accent': '240 10% 14%',
    '--sidebar-accent-foreground': '0 0% 98%',
    '--sidebar-border': '240 10% 18%',
  },
  light: {
    '--background': '240 10% 97%',
    '--foreground': '240 10% 10%',
    '--card': '0 0% 100%',
    '--card-foreground': '240 10% 10%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '240 10% 10%',
    '--secondary': '240 10% 93%',
    '--secondary-foreground': '240 10% 10%',
    '--muted': '240 10% 93%',
    '--muted-foreground': '240 5% 40%',
    '--border': '240 10% 86%',
    '--input': '240 10% 86%',
    '--destructive': '0 72% 45%',
    '--success': '152 65% 30%',
    '--warning': '38 92% 32%',
    '--danger': '0 72% 45%',
    '--info': '217 80% 45%',
    '--surface': '0 0% 100%',
    '--surface-elevated': '240 10% 93%',
    '--text-primary': '240 10% 10%',
    '--text-secondary': '240 6% 28%',
    '--text-muted': '240 5% 40%',
    '--border-strong': '240 10% 72%',
    '--overlay': '240 10% 2%',
    '--ranking-gold': '43 96% 36%',
    '--ranking-silver': '240 5% 45%',
    '--ranking-bronze': '25 75% 38%',
    '--sidebar-background': '0 0% 100%',
    '--sidebar-foreground': '240 10% 10%',
    '--sidebar-accent': '240 10% 93%',
    '--sidebar-accent-foreground': '240 10% 10%',
    '--sidebar-border': '240 10% 86%',
  },
};

const ARENA: SchemePalette = {
  dark: {
    '--background': '240 20% 4%',
    '--surface': '240 20% 7%',
    '--surface-elevated': '240 19% 9%',
    '--text-primary': '240 24% 93%',
    '--text-secondary': '231 9% 81%',
    '--text-muted': '240 8% 58%',
    '--border': '240 14% 16%',
    '--border-strong': '240 14% 24%',
    '--success': '162 100% 45%',
    '--warning': '45 84% 62%',
    '--danger': '349 100% 63%',
    ...SURFACE_ALIASES,
  },
  light: {
    '--background': '240 20% 97%',
    '--surface': '0 0% 100%',
    '--surface-elevated': '240 19% 94%',
    '--text-primary': '240 24% 9%',
    '--text-secondary': '231 9% 30%',
    '--text-muted': '240 8% 42%',
    '--border': '240 14% 86%',
    '--border-strong': '240 14% 74%',
    '--success': '162 100% 24%',
    '--warning': '45 84% 30%',
    '--danger': '349 80% 45%',
    ...SURFACE_ALIASES,
  },
};

const MODERN: SchemePalette = {
  dark: {
    '--background': '207 33% 9%',
    '--surface': '207 29% 14%',
    '--surface-elevated': '208 29% 20%',
    '--text-primary': '0 0% 97%',
    '--text-secondary': '203 15% 76%',
    '--text-muted': '200 15% 57%',
    '--border': '210 23% 20%',
    '--border-strong': '210 24% 29%',
    '--success': '153 64% 45%',
    '--warning': '48 100% 55%',
    '--danger': '358 74% 52%',
    '--info': '195 100% 50%',
    '--overlay': '207 45% 8%',
    '--radius': '0.75rem',
    '--ranking-gold': '48 100% 55%',
    '--ranking-silver': '203 8% 72%',
    '--ranking-bronze': '25 70% 50%',
    '--plan-free': '200 12% 58%',
    '--plan-elite': '48 100% 55%',
    '--plan-growth': '195 100% 50%',
    '--plan-scale': '215 84% 64%',
    '--plan-enterprise': '267 58% 68%',
    '--chart-1': '195 100% 50%',
    '--chart-2': '211 85% 63%',
    '--chart-3': '184 68% 48%',
    '--chart-4': '48 100% 55%',
    '--chart-5': '188 70% 50%',
    '--chart-6': '203 15% 76%',
    '--shadow-casino':
      '0 14px 30px -22px hsl(var(--overlay) / 0.95), inset 0 1px 0 hsl(var(--text-primary) / 0.035)',
    '--shadow-casino-active':
      '0 18px 34px -20px hsl(var(--accent) / 0.26), inset 0 1px 0 hsl(var(--text-primary) / 0.055)',
    '--shadow-casino-gold':
      '0 16px 32px -22px hsl(var(--warning) / 0.25), inset 0 1px 0 hsl(var(--warning) / 0.08)',
    '--shadow-accent-glow': '0 0 10px hsl(var(--accent) / 0.42)',
    ...SURFACE_ALIASES,
    '--muted': '208 25% 18%',
    '--input': '210 23% 23%',
  },
  light: {
    '--background': '207 33% 97%',
    '--surface': '0 0% 100%',
    '--surface-elevated': '207 28% 93%',
    '--text-primary': '207 45% 13%',
    '--text-secondary': '207 22% 31%',
    '--text-muted': '205 13% 45%',
    '--border': '207 20% 84%',
    '--border-strong': '207 20% 69%',
    '--success': '153 70% 30%',
    '--warning': '43 100% 39%',
    '--danger': '358 68% 46%',
    '--info': '199 82% 42%',
    '--overlay': '207 45% 8%',
    '--radius': '0.75rem',
    '--ranking-gold': '43 100% 39%',
    '--ranking-silver': '205 8% 48%',
    '--ranking-bronze': '25 70% 43%',
    '--plan-free': '205 10% 43%',
    '--plan-elite': '43 100% 39%',
    '--plan-growth': '195 100% 42%',
    '--plan-scale': '215 78% 48%',
    '--plan-enterprise': '267 42% 48%',
    '--chart-1': '195 100% 42%',
    '--chart-2': '207 72% 42%',
    '--chart-3': '185 66% 38%',
    '--chart-4': '43 100% 39%',
    '--chart-5': '188 78% 34%',
    '--chart-6': '165 18% 42%',
    '--shadow-casino':
      '0 14px 30px -22px hsl(var(--overlay) / 0.28), inset 0 1px 0 hsl(var(--accent-contrast) / 0.72)',
    '--shadow-casino-active':
      '0 18px 34px -20px hsl(var(--accent) / 0.28), inset 0 1px 0 hsl(var(--accent-contrast) / 0.82)',
    '--shadow-casino-gold':
      '0 16px 32px -22px hsl(var(--warning) / 0.32), inset 0 1px 0 hsl(var(--warning) / 0.1)',
    '--shadow-accent-glow': '0 0 10px hsl(var(--accent) / 0.28)',
    ...SURFACE_ALIASES,
    '--muted': '207 24% 91%',
    '--input': '207 20% 78%',
  },
};

export const THEME_TOKENS: Readonly<Record<LauncherTheme, SchemePalette>> = {
  minimal: MINIMAL,
  arena: ARENA,
  modern: MODERN,
};

export const THEME_ACCENTS: Readonly<Partial<Record<LauncherTheme, string>>> = {
  arena: '#6E5BFF',
  modern: '#00A1D6',
};

const paletteFor = (
  theme: LauncherTheme,
  scheme: ColorScheme,
): ThemeTokens | undefined =>
  Object.hasOwn(THEME_TOKENS, theme) ? THEME_TOKENS[theme][scheme] : undefined;

export const themeTokens = (
  theme: LauncherTheme,
  tenantAccent: string,
  scheme: ColorScheme = 'dark',
): ThemeTokens => {
  const palette = paletteFor(theme, scheme);
  if (!palette) return {};

  const defaultAccent = Object.hasOwn(THEME_ACCENTS, theme)
    ? THEME_ACCENTS[theme]
    : undefined;
  const accent = defaultAccent
    ? (primaryTokens(tenantAccent) ?? primaryTokens(defaultAccent))
    : null;
  if (!accent) return { ...palette };

  return {
    ...palette,
    '--accent': accent.primary,
    '--accent-contrast': accent.foreground,
  };
};
